import { getTrackingParams } from './tracking';

const SUMOLOGIC_ENDPOINT = 'https://s8t.teads.tv/logs/studio/frontend';
const START_TIME = getTimestamp();
const SESSION_ID = generateSessionId();

const SAMPLING_RATE = 1 / 10;
const CAN_SEND_LOGS = Math.random() < SAMPLING_RATE;

export enum SumoLevel {
  INFO = 'info',
  ERROR = 'error',
}

interface SumoLogInfo {
  ci_uuid: string;
  t: string;
  ua: string;
  d?: 'm' | 'd';
  o?: string;
  b?: string;
  bv?: string;
  e: string;
  em?: string;
  or: string;
  env?: string;
  pid?: string;
}

function getTimestamp() {
  return performance.now() + performance.timeOrigin;
}

function formatTimestamp(ts: number) {
  return (ts / 1000).toFixed(3);
}

type SumoEvent = { ci: string } & Pick<SumoLogInfo, 'e' | 'em'>;

function getLogMessage({ ci, e, em }: SumoEvent): SumoLogInfo {
  const uad = navigator.userAgentData;

  const b = uad?.brands
    .map((b) => ({ brand: b.brand, version: parseInt(b.version) }))
    .reduce((a, b) => (a.version > b.version ? a : b), { brand: '', version: Number.NEGATIVE_INFINITY });

  const trackingParams = getTrackingParams();
  const base: Omit<SumoLogInfo, keyof SumoEvent | 'ci_uuid'> = {
    t: formatTimestamp(getTimestamp() - START_TIME),
    ua: navigator.userAgent,
    d: uad ? (uad.mobile ? 'm' : 'd') : undefined,
    o: uad?.platform,
    b: b?.brand,
    bv: b?.version?.toString(),
    or: location.origin ?? null,
    env: trackingParams.env,
    pid: trackingParams.pid?.toString(),
  };

  return {
    ...base,
    ci_uuid: ci,
    e,
    em,
  };
}

function generateSessionId() {
  const rand = () => (Math.random() * 1000).toFixed(0);

  return `noAuctId-${rand()}${rand()}${rand()}`;
}

function getLogLine(info: SumoLogInfo, level: SumoLevel) {
  const timestamp = getTimestamp().toFixed(0);

  const sessionId = getTrackingParams().auctid ?? SESSION_ID;
  const message = JSON.stringify(info);

  return `[${timestamp}][${sessionId}][${level}] ${message}`;
}

function getUrl(message: string) {
  return `${SUMOLOGIC_ENDPOINT}?${encodeURIComponent(message)}`;
}

function triggerPixel(message: string) {
  try {
    const result = navigator.sendBeacon?.(SUMOLOGIC_ENDPOINT, message) ?? false;
    if (!result) {
      new Image().src = getUrl(message);
    }
  } catch (e) {
    console.error(e);
  }
}

export function sumo(event: SumoEvent, level: SumoLevel = SumoLevel.INFO) {
  const logMessage = getLogMessage(event);
  const logLine = getLogLine(logMessage, level);

  if (CAN_SEND_LOGS) {
    console.debug(`Sumo log:\n\n${logLine}`);
    triggerPixel(logLine);
  } else {
    console.debug(`All sumo logs will be suppressed from triggering this session.\n\n${logLine}`);
  }
}
